import type { Engine } from 'src/services/people/peopleApiService.types'
import {
  CURRENT_PERSON_ID,
  ENTRY_POINTS,
  type Circuits,
} from 'src/services/request.const'
import {
  INCLUDE_CLAUSES,
  OBJECT_TYPES,
  type ObjectType,
} from 'src/tpbp-configuration/models/models.const'
import type { Scalar } from 'src/tpbp-core/models/global.type'
import type { BACKEND_LEVERS_NAMES } from 'src/tpbp-json-api-handler/models/lever/lever.model'

export const ENDPOINTS = {
  person: (userId = CURRENT_PERSON_ID) => `${ENTRY_POINTS.people}/${userId}`,
  personResource: (personId: string, type: string, id?: string) => {
    const base = `${ENTRY_POINTS.people}/${personId}/${type}`
    return id ? `${base}/${id}` : base
  },
  additionalTaxInformation: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/additional_tax_informations`,
  circuits: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/circuits`,
  beneficiaryClausesConfiguration: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/beneficiary_clauses/configurations`,
  budgetItemsPortfolioRelationshipsAll: () =>
    `${ENTRY_POINTS.budgetItems}/relationships/${ENTRY_POINTS.portfolios}`,
  budgetItemsPortfolioRelationships: (budgetItemId: string) =>
    `${ENTRY_POINTS.budgetItems}/${budgetItemId}/relationships/${ENTRY_POINTS.portfolios}`,
  donations: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/donations`,
  donationsConfigurations: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/donations/configurations`,
  updateDonation: (donationId: string) => `/donations/${donationId}`,
  createDonation: () => `/donations`,
  deleteDonation: (donationId: string) => `/donations/${donationId}`,
  patrimonialIndicators: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/patrimonial_indicators`,
  legacies: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/legacies`,
  legaciesConfigurations: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/legacies/configurations`,
  updateLegacies: (legId: string) => `/legacies/${legId}`,
  createLegacies: () => `/legacies`,
  deleteLegacies: (legId: string) => `/legacies/${legId}`,
  portfolios: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/${ENTRY_POINTS.portfolios}`,
  shortCircuit: (subPath: string) => `/${ENTRY_POINTS.shortCircuit}/${subPath}`,
  portfolioRelationships: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/relationships/${ENTRY_POINTS.portfolios}`,
  dashboardData: () => `${ENTRY_POINTS.portfolios}/dashboard_data`,
  portfolioVersions: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/versions`,
  investments: () => 'investments',
  portfolioValuationsTimeseries: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/valuations/timeseries`,
  portfolioTransactions: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/transactions`,
  portfoliosConfigurations: () => `/${ENTRY_POINTS.portfolios}/configurations`,
  updatePortfolio: (portfolioId: string) =>
    `/${ENTRY_POINTS.portfolios}/${portfolioId}`,
  upsertBeneficiaryClauses: (portfolioId: string, userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/${ENTRY_POINTS.portfolios}/${portfolioId}/beneficiary_clauses`,
  budgetItemAggregate: () => `${ENTRY_POINTS.budgetItems}/aggregate`,
  budgetItemConfigurations: () => `${ENTRY_POINTS.budgetItems}/configurations`,
  budgetItem: (budgetItemId: string) =>
    `${ENTRY_POINTS.budgetItems}/${budgetItemId}`,
  budgetItems: () => `${ENTRY_POINTS.budgetItems}`,
  personProfessions: (personId: string, professionId?: string) =>
    `${ENTRY_POINTS.people}/${personId}/${ENTRY_POINTS.professions}/${professionId ?? ''}`,
  studies: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/studies`,
  studiesIndicator: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/studies/indicator`,
  personAdvisingReports: (personId: string, studyId: string) =>
    `${ENTRY_POINTS.people}/${personId}/studies/${studyId}/advising_reports`,
  personRelationsMap: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/relationships/relations_map`,
  personCareer: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/${ENTRY_POINTS.career}`,
  personRetirementFunds: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/${ENTRY_POINTS.career}/${ENTRY_POINTS.retirementFunds}`,
  personRetirementTrimestersBuyback: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/${ENTRY_POINTS.career}/${ENTRY_POINTS.retirementTrimestersBuyback}`,
  personExternalData: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/external_data`,
  personCalculator: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/${OBJECT_TYPES.loans}/calculator`,
  personCallToAction: () => `${ENTRY_POINTS.people}/call_to_action`,
  tokens: () => `${ENTRY_POINTS.tokens}`,
  linxoAddGlobalConsent: (userId: string) =>
    `v1/oxlin/users/${userId}/add_global_consent`,
  linxoSynchronize: (userId: string) =>
    `v1/oxlin/users/${userId}/synchronize_backend`,
  linxoAccounts: (userId: string, accountId?: string) =>
    `v1/oxlin/users/${userId}/accounts${accountId ? `/${accountId}` : ''}`,
  linxoAccountsAllListStatus: (userId: string) =>
    `v1/oxlin/users/${userId}/accounts/all/list_status`,
  linxoEditCredentials: (userId: string, accountId: string) =>
    `v1/oxlin/users/${userId}/accounts/${accountId}/edit_credentials`,
  linxoManageAccounts: (userId: string, accountId: string) =>
    `v1/oxlin/users/${userId}/accounts/${accountId}/manage_accounts`,
  linxoAggregate: (userId: string) => `v1/oxlin/users/${userId}/aggregate`,
  oauthRevoke: () => 'v1/oauth/revoke',
  oauthToken: () => 'v1/oauth/token',
  agenda: (endpoint: string) => `agenda/${endpoint}`,
  agendaAppointmentDetails: () => `agenda/appointment-details`,
  agendaAppointments: () => `agenda/appointments`,
  agendaClientAccess: () => `agenda/client-access`,
  agendaFinancialAdvisorAvailabilities: (advisorId: string) =>
    `agenda/financial-advisors/${advisorId}/availabilities`,
  agendaFinancialAdvisors: () => `agenda/financial-advisors`,
  agendaReasons: () => `agenda/reasons`,
  userPreference: (userId: string) => `users/${userId}/preference`,
  products: () => `${ENTRY_POINTS.products}`,
  productsConfigurations: () => `${ENTRY_POINTS.products}/configurations`,
  productsCategoryConfigurations: () =>
    `${ENTRY_POINTS.products}/category_configurations`,
  pedagogicalContents: () => `${ENTRY_POINTS.pedagogicalContents}`,
  calculator: () => `${ENTRY_POINTS.calculator}`,
  aggregate: () => `${ENTRY_POINTS.portfolios}/aggregate`,
  notify: () => `${ENTRY_POINTS.people}/notify`,
  indicators: (userId: string) =>
    `${ENTRY_POINTS.people}/${userId}/patrimonial_indicators`,
  investorProfile: (userId: string) =>
    `${ENTRY_POINTS.people}/${userId}/investor_profile`,
  validateInvestorProfile: (userId = CURRENT_PERSON_ID) =>
    `${ENTRY_POINTS.people}/${userId}/investor_profile/qualifications/validate_score`,
  linkedPortfolios: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/linked_portfolios`,
  linkablePortfolios: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/linkable_portfolios`,
  portfolioLinkableBudgetItems: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/linkable_budget_items`,
  portfolioLinkedBudgetItems: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/linked_budget_items`,
  portfolioLines: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/lines`,
  portfolioEstimationData: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/estimation_data`,
  portfolioEsgData: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/esg_data`,
  portfolioBeneficiaries: (portfolioId: string) =>
    `${ENTRY_POINTS.portfolios}/${portfolioId}/beneficiaries`,
  ownership: (ownershipId: string) => `ownerships/${ownershipId}`,
  investorProfileSurvey: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/investor_profile/qualifications/survey`,
  investorProfileComputeScore: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/investor_profile/qualifications/compute_score`,
  investorProfileValidateScore: (personId: string) =>
    `${ENTRY_POINTS.people}/${personId}/investor_profile/qualifications/validate_score`,
  estimatesSimulate: () => '/estimates/simulate',
  personStudy: (personId: string, studyId: string) =>
    `${ENTRY_POINTS.people}/${personId}/studies/${studyId}`,
  portfolioRestructurer: (personId: string, portfolioId: string) =>
    `${ENTRY_POINTS.people}/${personId}/portfolios/${portfolioId}/restructurer`,
  pdfGenerations: (personId: string, studyId: string) =>
    `${ENTRY_POINTS.people}/${personId}/studies/${studyId}/pdf_generations`,
  pdfGenerationStatus: (
    personId: string,
    studyId: string,
    pdfGenerationId: string,
  ) =>
    `${ENTRY_POINTS.people}/${personId}/studies/${studyId}/pdf_generations/${pdfGenerationId}`,
  recordType: (type: string, ressource?: string) =>
    ressource ? `${type}/${ressource}` : type,
  relationship: (
    type: string,
    id: string | undefined,
    type2: string,
    id2?: string,
  ) => {
    const entryPointUrl = id ? `${type}/${id}` : type
    const entryPointUrl2 = id2 ? `${type2}/${id2}` : type2
    return `${entryPointUrl}/${entryPointUrl2}`
  },
  custom: () => '',
} as const satisfies Record<string, (...arguments_: string[]) => string>

type EndpointKeys = keyof typeof ENDPOINTS
type CircuitsIncludes = 'levers'

type IncludeClauses = typeof INCLUDE_CLAUSES

type OtherIncludes = ValueOf<IncludeClauses>

export type InvestorProfileIncludes = ValueOf<
  Pick<IncludeClauses, 'esgQualification'>
>

export type PortfoliosIncludes = ValueOf<
  Pick<
    IncludeClauses,
    | 'beneficiaries'
    | 'lines'
    | 'linkedPortfolios'
    | 'linkablePortfolios'
    | 'linkedBudgetItems'
    | 'linkableBudgetItems'
    | 'ownerships'
  >
>
export type PortfoliosIncludesA = ValueOf<
  Pick<
    IncludeClauses,
    | 'beneficiaries'
    | 'lines'
    | 'linkedPortfolios'
    | 'linkablePortfolios'
    | 'linkedBudgetItems'
    | 'linkableBudgetItems'
    | 'ownerships'
  >
>

export type PortfoliosEstimationDataIncludes = ValueOf<
  Pick<
    IncludeClauses,
    | 'rentEstimate'
    | 'saleEstimate'
    | 'saleEstimateSeries'
    | 'saleEstimateSeriesMax'
    | 'saleEstimateSeriesMin'
  >
>

type PortfoliosConfigurationsIncludes = ValueOf<
  Pick<
    IncludeClauses,
    | 'productGroupConfiguration'
    | 'productCategoryConfiguration'
    | 'productGroupMacroConfiguration'
  >
>

type DonationsIncludes = 'donees' | 'donator' | 'donation_donees'

type LegsIncludes =
  | 'attributor'
  | 'legacy_receivers'
  | 'attributors'
  | 'receivers'

type AllPossibleQueryParameters =
  | 'fields'
  | 'filters'
  | 'extended_portfolios'
  | 'circuit'
  | 'engine'
  | 'lever'
  | 'levers'
  | 'personIds'
  | 'study'
  | 'amount'
  | 'period'
  | 'monthly_payment'
  | 'attribute_changed'
  | 'horizon_max'
  | 'term_date'
  | 'retirement_fund'
  | 'option'
  | 'product_id'
  | 'custom'

export type AllPossibleFilters =
  | 'indicator'
  | 'indicators_group'
  | 'circuit'
  | 'kind'
  | 'product_kind'
  | 'product_kinds'
  | 'operation_types'
  | 'year'

export type AllPossibleFieldsRessource = 'portfolios' | 'studies' | 'people'

type AllPossibleLevers =
  (typeof BACKEND_LEVERS_NAMES)[keyof typeof BACKEND_LEVERS_NAMES]

export const INCLUDES = {
  person: (includes: string[]) => mapIncludes(includes),
  personResource: () => '',
  additionalTaxInformation: () => '',
  circuits: (includes: CircuitsIncludes[]) => mapIncludes(includes),
  beneficiaryClausesConfiguration: () => '',
  budgetItemsPortfolioRelationshipsAll: () => '',
  budgetItemsPortfolioRelationships: () => '',
  donations: (includes: DonationsIncludes[]) => mapIncludes(includes),
  donationsConfigurations: (includes: DonationsIncludes[]) =>
    mapIncludes(includes),
  updateDonation: () => '',
  createDonation: () => '',
  deleteDonation: () => '',
  patrimonialIndicators: () => '',
  legacies: (includes: LegsIncludes[]) => mapIncludes(includes),
  legaciesConfigurations: (includes: LegsIncludes[]) => mapIncludes(includes),
  updateLegacies: () => '',
  createLegacies: () => '',
  deleteLegacies: () => '',
  portfolios: (includes: PortfoliosIncludes[]) => mapIncludes(includes),
  portfolioRelationships: () => '',
  dashboardData: () => '',
  portfolioVersions: (includes: string[]) => mapIncludes(includes),
  investments: () => '',
  portfolioValuationsTimeseries: () => '',
  portfolioTransactions: () => '',
  portfoliosConfigurations: (includes: PortfoliosConfigurationsIncludes[]) =>
    mapIncludes(includes),
  updatePortfolio: (includes: PortfoliosIncludes[]) => mapIncludes(includes),
  upsertBeneficiaryClauses: () => '',
  budgetItemAggregate: (includes: string[]) => mapIncludes(includes),
  budgetItemConfigurations: (includes: string[]) => mapIncludes(includes),
  budgetItem: (includes: string[]) => mapIncludes(includes),
  budgetItems: (includes: string[]) => mapIncludes(includes),
  personProfessions: () => '',
  studies: () => '',
  studiesIndicator: (includes: string[]) => mapIncludes(includes),
  personAdvisingReports: () => '',
  personRelationsMap: () => '',
  personCareer: () => '',
  personRetirementFunds: (includes: string[]) => mapIncludes(includes),
  personRetirementTrimestersBuyback: (includes: string[]) =>
    mapIncludes(includes),
  personExternalData: () => '',
  personCalculator: () => '',
  personCallToAction: () => '',
  tokens: () => '',
  linxoAddGlobalConsent: () => '',
  linxoSynchronize: () => '',
  linxoAccounts: () => '',
  linxoAccountsAllListStatus: () => '',
  linxoEditCredentials: () => '',
  linxoManageAccounts: () => '',
  linxoAggregate: () => '',
  oauthRevoke: () => '',
  oauthToken: () => '',
  agenda: (includes: string[]) => mapIncludes(includes),
  agendaAppointmentDetails: (includes: string[]) => mapIncludes(includes),
  agendaAppointments: (includes: string[]) => mapIncludes(includes),
  agendaClientAccess: (includes: string[]) => mapIncludes(includes),
  agendaFinancialAdvisorAvailabilities: (includes: string[]) =>
    mapIncludes(includes),
  agendaFinancialAdvisors: (includes: string[]) => mapIncludes(includes),
  agendaReasons: (includes: string[]) => mapIncludes(includes),
  userPreference: () => '',
  products: () => '',
  productsConfigurations: () => '',
  productsCategoryConfigurations: () => '',
  pedagogicalContents: () => '',
  calculator: () => '',
  aggregate: () => '',
  notify: () => '',
  indicators: () => '',
  investorProfile: (
    includes: InvestorProfileIncludes[] = [INCLUDE_CLAUSES.esgQualification],
  ) => mapIncludes(includes),
  validateInvestorProfile: () => '',
  linkedPortfolios: () => '',
  linkablePortfolios: () => '',
  portfolioLinkableBudgetItems: () => '',
  portfolioLinkedBudgetItems: () => '',
  portfolioLines: () => '',
  portfolioEstimationData: (includes: OtherIncludes[]) => mapIncludes(includes),
  portfolioEsgData: () => '',
  portfolioBeneficiaries: () => '',
  ownership: () => '',
  investorProfileSurvey: () => '',
  investorProfileComputeScore: () => '',
  investorProfileValidateScore: () => '',
  estimatesSimulate: () => '',
  personStudy: (includes: string[]) => mapIncludes(includes),
  portfolioRestructurer: () => '',
  pdfGenerations: () => '',
  pdfGenerationStatus: () => '',
  recordType: () => '',
  relationship: () => '',
  shortCircuit: () => '',
  custom: (includes: string[]) => mapIncludes(includes),
} as const satisfies Record<EndpointKeys, (...parameters: never[]) => string>

function mapIncludes(includes: string[]) {
  if (includes.length === 0) return ''
  return `include=${includes.join(',')}`
}

export const QUERY_PARAMS = {
  fields: (
    ressource: AllPossibleFieldsRessource,
    values?: string[] | undefined,
  ) => (values ? `fields[${ressource}]=${values?.join(',')}` : ''),
  filters: (filter: AllPossibleFilters, values?: string[] | undefined) =>
    values && values.length > 0 ? `filter[${filter}]=${values.join(',')}` : '',
  extended_portfolios: (extended = true) => `extended_portfolios=${extended}`,
  circuit: (circuitId: Circuits) => `circuit=${circuitId}`,
  engine: (engine: Engine) => `engine=${engine}`,
  lever: (lever: AllPossibleLevers) => `lever=${lever}`,
  levers: (levers: AllPossibleLevers[]) => `levers=${levers.join(',')}`,
  personIds: (personIds: string[]) =>
    personIds.length > 0 ? `person_ids=${personIds.join(',')}` : '',
  study: (study?: string) => (study ? `study_id=${study}` : ''),
  amount: (amount: number) => `amount=${amount}`,
  period: (period: number) => `period=${period}`,
  monthly_payment: (monthly_payment: number) =>
    `monthly_payment=${monthly_payment}`,
  attribute_changed: (attribute_changed: string) =>
    `attribute_changed=${attribute_changed}`,
  horizon_max: (horizon_max: number) => `horizon_in_months_max=${horizon_max}`,
  term_date: (term_date: string) => `term_date=${term_date}`,
  retirement_fund: (retirement_fund?: Scalar) =>
    retirement_fund ? `retirement_fund=${retirement_fund}` : '',
  option: (option?: Scalar) => (option ? `option=${option}` : ''),
  product_id: (product_id: string) => `product_id=${product_id}`,
  custom: (key: string, value: string | number | boolean | null | undefined) =>
    value === undefined || value === null || value === ''
      ? ''
      : `${key}=${value}`,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} satisfies Record<AllPossibleQueryParameters, (...arguments_: any) => string>

type PickFieldsParameters = {
  fields: string[]
}

type CurrentlySupportedPickableRessources = Extract<
  ObjectType,
  'people' | 'studies'
>
type PickFieldsType = {
  [Ressource in CurrentlySupportedPickableRessources]: (
    parameters: PickFieldsParameters,
  ) => `fields[${Ressource}]=${string}`
}

export const PICK_FIELDS = {
  people: ({ fields }) => `fields[people]=${fields.join(',')}`,
  studies: ({ fields }) => `fields[studies]=${fields.join(',')}`,
} satisfies PickFieldsType

export function computeQueryParameters(queryParameters: string[]): string {
  return queryParameters.filter((queryParameter) => !!queryParameter).join('&')
}

export function computeUrlWithQueryParameters(
  endpoint: string,
  queryParameters: string[] = [],
) {
  const queryString = computeQueryParameters(queryParameters)
  if (!queryString) {
    return endpoint
  }
  return `${endpoint}?${queryString}`
}
